/**
 * v109.9.16 — Travel · Advance Settlement tab
 * -------------------------------------------
 * Replaces the generic RecordsPage on the settlement tab. Every active
 * employee gets a "File Settlement" button that opens the massive
 * TravelSettlementFormDialog.
 *
 * v109.9.176 — "Details" now opens the same form dialog (read-only when
 * the row is past Draft/Downloaded/Rejected). The bill-verification drawer
 * and every `verify-item` code path have been removed.
 */
import { useEffect, useMemo, useState } from "react";
import api, { formatError } from "../lib/api";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Plus, ClipboardList, ExternalLink, Landmark, Eye, Pencil, Send, Info, Trash2 } from "lucide-react";
import { useGlobalViewMode } from "../components/ViewModeToggle";
import TravelSettlementFormDialog, { InstructionsBanner } from "../components/TravelSettlementFormDialog";
import { useDataChange } from "../lib/events";

const STATUS_TONE = {
  Draft:             "bg-slate-100 text-slate-700 border-slate-200",
  Downloaded:        "bg-blue-100 text-blue-800 border-blue-200",
  Submitted:         "bg-amber-100 text-amber-800 border-amber-200",
  AssignerApproved:  "bg-cyan-100 text-cyan-800 border-cyan-200",
  ManagerApproved:   "bg-cyan-100 text-cyan-800 border-cyan-200",
  Approved:          "bg-emerald-100 text-emerald-800 border-emerald-200",
  Ledgered:          "bg-violet-100 text-violet-800 border-violet-200",
  Rejected:          "bg-rose-100 text-rose-700 border-rose-200",
};

function money(v) { return `₹ ${Number(v || 0).toLocaleString("en-IN")}`; }

export default function TravelSettlementsTab({ actionPulse = null }) {
  const { user } = useAuth();
  const [viewMode] = useGlobalViewMode();
  const uid = String(user?.id || "");
  // v109.9.173 — Advance Settlement tab now shows ONLY the current
  // employee's own entries. Sub-tabs removed; approvers see requests in
  // the Approval Tab instead.
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  const [formOpen, setFormOpen] = useState(false);
  const [editRow, setEditRow] = useState(null);
  const [submitRow, setSubmitRow] = useState(null);
  const [instructionsFor, setInstructionsFor] = useState(null); // { instructions, row }
  const [confirmDelete, setConfirmDelete] = useState(null);

  // v109.9.176 — Details = open the same form dialog with the record loaded.
  const openDetails = (r) => { setEditRow(r); setFormOpen(true); };

  const load = async () => {
    setBusy(true);
    try {
      const { data } = await api.get(`/travel-settlements?scope=mine`);
      setRows(Array.isArray(data) ? data : []);
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail));
    } finally { setBusy(false); }
  };
  useEffect(() => { load(); }, []);

  // v109.9.179 — Refresh when Accounts (or anyone) changes any settlement
  // elsewhere in the app so the employee sees updated numbers instantly.
  useDataChange(["travel:updated", "travel:settlement:updated", "travel:disbursed", "travel:ledgered"], () => load());

  const doDelete = async () => {
    if (!confirmDelete) return;
    try {
      await api.delete(`/travel-settlements/${confirmDelete.id}`);
      toast.success("Draft settlement deleted");
      setConfirmDelete(null);
      load();
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail));
    }
  };

  const viewInstructions = async (row) => {
    try {
      const { data } = await api.get(`/travel-settlements/${row.id}/instructions`);
      setInstructionsFor({ instructions: data, row });
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail));
    }
  };

  // React to Dashboard "Advance Settlement" quick-action — auto-open the form.
  useEffect(() => {
    if (actionPulse && actionPulse.key === "new") {
      setEditRow(null);
      setFormOpen(true);
    }
  }, [actionPulse]);

  return (
    <div className="space-y-4">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2">
        <div className="text-[11.5px] text-slate-500">
          Showing only your own settlements. Approvers see requests in the <b>Approval</b> tab.
        </div>
        <Button
          className="bg-indigo-600 hover:bg-indigo-700 text-white"
          onClick={() => { setEditRow(null); setFormOpen(true); }}
          data-testid="file-settlement-btn"
        >
          <Plus size={14} className="mr-1.5" /> File Settlement
        </Button>
      </div>

      <div className="rounded-lg border border-slate-200 dark:border-slate-700 overflow-hidden bg-white dark:bg-slate-900">
        {viewMode === "card" ? (
          rows.length === 0 ? (
            <div className="p-8 text-center text-slate-500">{busy ? "Loading…" : "No settlements to show."}</div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3 p-3" data-testid="ts-cards">
              {rows.map((r) => {
                const isOwner = r.created_by_id === uid;
                return (
                  <div key={r.id} className="pk-card p-4 hover:shadow-md transition-shadow" data-testid={`ts-card-${r.id}`}>
                    <div className="flex items-start justify-between gap-2 mb-2">
                      <div className="min-w-0 flex-1">
                        <div className="font-bold text-slate-900 dark:text-white truncate">{r.created_by_name}</div>
                        <div className="text-[10.5px] text-slate-500 truncate">{r.employee_code || ""}</div>
                      </div>
                      <Badge className={`${STATUS_TONE[r.status] || "bg-slate-100"} text-[10px] shrink-0`}>{r.status}</Badge>
                    </div>
                    <div className="grid grid-cols-2 gap-2 text-[11.5px] mb-2">
                      <div><div className="text-[9.5px] uppercase tracking-wider text-slate-500 font-semibold">Kind</div><div><Badge variant="outline" className="text-[10px]">{r.kind}</Badge></div></div>
                      <div><div className="text-[9.5px] uppercase tracking-wider text-slate-500 font-semibold">Amount</div><div className="mono font-semibold text-slate-900">{money(r.claim_amount)}</div></div>
                      <div className="col-span-2"><div className="text-[9.5px] uppercase tracking-wider text-slate-500 font-semibold">{r.kind === "local" ? "Month" : "Purpose"}</div><div className="text-slate-800 line-clamp-2">{r.kind === "local" ? `${r.month} ${r.year}` : r.purpose_of_travel}</div></div>
                    </div>
                    {(r.approvals || []).length > 0 && (
                      <div className="mb-2">
                        <div className="text-[9.5px] uppercase tracking-wider text-slate-500 font-semibold mb-1">Approval Chain</div>
                        <div className="flex flex-wrap gap-1">
                          {(r.approvals || []).map((a, i) => (
                            <Badge key={i} className={`text-[9.5px] ${a.status === "approved" ? "bg-emerald-100 text-emerald-800 border-emerald-200" : a.status === "rejected" ? "bg-rose-100 text-rose-700 border-rose-200" : "bg-amber-100 text-amber-800 border-amber-200"}`}>
                              {a.stage === "assigned_by" ? "Assigner" : a.stage === "reporting_manager" ? "RM" : "Accounts"}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    )}
                    <div className="pt-2 border-t border-slate-100 dark:border-slate-800 flex flex-wrap gap-1.5">
                      <Button size="sm" variant="outline" onClick={() => openDetails(r)} className="h-7 text-[10.5px]" data-testid={`ts-open-card-${r.id}`}>
                        <Eye size={11} className="mr-1" /> Details
                      </Button>
                      {r.settlement_code && (
                        <Badge variant="outline" className="text-[10px] font-mono">{r.settlement_code}</Badge>
                      )}
                      {isOwner && ["Draft", "Downloaded", "Rejected"].includes(r.status) && (
                        <Button size="sm" variant="outline" onClick={() => { setEditRow(r); setFormOpen(true); }} className="h-7 text-[10.5px]" data-testid={`ts-edit-card-${r.id}`}>
                          <Pencil size={11} className="mr-1" /> Edit
                        </Button>
                      )}
                      {isOwner && r.status === "Draft" && (
                        <Button size="sm" variant="outline" onClick={() => setConfirmDelete(r)} className="h-7 text-[10.5px] text-rose-600 border-rose-200 hover:bg-rose-50" data-testid={`ts-delete-card-${r.id}`}>
                          <Trash2 size={11} className="mr-1" /> Delete
                        </Button>
                      )}
                      {isOwner && r.status === "Downloaded" && (
                        <>
                          <Button size="sm" variant="outline" onClick={() => viewInstructions(r)} className="h-7 text-[10.5px]" data-testid={`ts-view-inst-card-${r.id}`}>
                            <Info size={11} className="mr-1" /> View Instructions
                          </Button>
                          <Button size="sm" onClick={() => setSubmitRow(r)} className="h-7 text-[10.5px] bg-indigo-600 hover:bg-indigo-700 text-white" data-testid={`ts-submit-approval-card-${r.id}`}>
                            <Send size={11} className="mr-1" /> Submit for Approval
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )
        ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-[12.5px]" data-testid="ts-table">
            <thead className="bg-slate-50 dark:bg-slate-800/60 text-[10.5px] uppercase tracking-wider text-slate-600 dark:text-slate-300">
              <tr>
                <th className="p-2.5 text-left">Employee</th>
                <th className="p-2.5 text-left">Kind</th>
                <th className="p-2.5 text-left">Purpose / Month</th>
                <th className="p-2.5 text-right">Amount</th>
                <th className="p-2.5 text-left">Approval Chain</th>
                <th className="p-2.5 text-center">Status</th>
                <th className="p-2.5 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr><td colSpan={7} className="p-8 text-center text-slate-500">
                  {busy ? "Loading…" : "No settlements to show."}
                </td></tr>
              ) : rows.map((r) => {
                const isOwner = r.created_by_id === uid;
                return (
                  <tr key={r.id} className="border-t border-slate-100 dark:border-slate-800 hover:bg-slate-50/60 dark:hover:bg-slate-800/40" data-testid={`ts-row-${r.id}`}>
                    <td className="p-2.5">
                      <div className="font-semibold">{r.created_by_name}</div>
                      <div className="text-[10.5px] text-slate-500">{r.employee_code || ""}</div>
                    </td>
                    <td className="p-2.5"><Badge variant="outline" className="text-[10px]">{r.kind}</Badge></td>
                    <td className="p-2.5">
                      {r.kind === "local"
                        ? <span>{r.month} {r.year}</span>
                        : <span className="line-clamp-2">{r.purpose_of_travel}</span>}
                    </td>
                    <td className="p-2.5 text-right font-mono font-semibold">{money(r.claim_amount)}</td>
                    <td className="p-2.5">
                      <div className="flex flex-wrap gap-1">
                        {(r.approvals || []).map((a, i) => (
                          <Badge key={i} className={`text-[10px] ${
                            a.status === "approved" ? "bg-emerald-100 text-emerald-800 border-emerald-200" :
                            a.status === "rejected" ? "bg-rose-100 text-rose-700 border-rose-200" :
                            "bg-amber-100 text-amber-800 border-amber-200"
                          }`}>
                            {a.stage === "assigned_by" ? "Assigner" : a.stage === "reporting_manager" ? "RM" : "Accounts"}
                          </Badge>
                        ))}
                      </div>
                    </td>
                    <td className="p-2.5 text-center"><Badge className={`${STATUS_TONE[r.status] || "bg-slate-100"} text-[10.5px]`}>{r.status}</Badge></td>
                    <td className="p-2.5 text-right">
                      <div className="flex justify-end gap-1 flex-wrap">
                        <Button size="sm" variant="outline" onClick={() => openDetails(r)} className="h-7 text-[10.5px]" data-testid={`ts-open-${r.id}`}>
                          <Eye size={11} className="mr-1" /> Details
                        </Button>
                        {isOwner && ["Draft", "Downloaded", "Rejected"].includes(r.status) && (
                          <Button size="sm" variant="outline" onClick={() => { setEditRow(r); setFormOpen(true); }} className="h-7 text-[10.5px]" data-testid={`ts-edit-${r.id}`}>
                            <Pencil size={11} className="mr-1" /> Edit
                          </Button>
                        )}
                        {isOwner && r.status === "Draft" && (
                          <Button size="sm" variant="outline" onClick={() => setConfirmDelete(r)} className="h-7 text-[10.5px] text-rose-600 border-rose-200 hover:bg-rose-50" data-testid={`ts-delete-${r.id}`}>
                            <Trash2 size={11} className="mr-1" /> Delete
                          </Button>
                        )}
                        {isOwner && r.status === "Downloaded" && (
                          <>
                            <Button size="sm" variant="outline" onClick={() => viewInstructions(r)} className="h-7 text-[10.5px]" data-testid={`ts-view-inst-${r.id}`}>
                              <Info size={11} className="mr-1" /> Instructions
                            </Button>
                            <Button size="sm" onClick={() => setSubmitRow(r)} className="h-7 text-[10.5px] bg-indigo-600 hover:bg-indigo-700 text-white" data-testid={`ts-submit-approval-${r.id}`}>
                              <Send size={11} className="mr-1" /> Submit for Approval
                            </Button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        )}
      </div>

      <TravelSettlementFormDialog
        open={formOpen}
        onClose={() => { setFormOpen(false); setEditRow(null); }}
        onSaved={load}
        editRecord={editRow}
      />

      {submitRow && (
        <SubmitForApprovalDialog
          row={submitRow}
          onClose={() => setSubmitRow(null)}
          onSubmitted={() => { setSubmitRow(null); load(); }}
        />
      )}

      {instructionsFor && (
        <InstructionsBanner
          instructions={instructionsFor.instructions}
          onCancel={() => setInstructionsFor(null)}
          onConfirm={() => setInstructionsFor(null)}
        />
      )}

      {/* v109.9.173 — Owner delete confirmation (Draft only). */}
      <Dialog open={!!confirmDelete} onOpenChange={(v) => { if (!v) setConfirmDelete(null); }}>
        <DialogContent className="max-w-md" data-testid="ts-delete-dialog">
          <DialogHeader>
            <DialogTitle className="heading text-rose-700">Delete Draft Settlement</DialogTitle>
          </DialogHeader>
          <div className="py-2 text-[12.5px] text-slate-700 dark:text-slate-200">
            Permanently delete your draft settlement{" "}
            <b className="mono">{confirmDelete?.settlement_code || ""}</b>?
            This cannot be undone.
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setConfirmDelete(null)}>Cancel</Button>
            <Button
              onClick={doDelete}
              className="bg-rose-600 hover:bg-rose-700 text-white"
              data-testid="ts-delete-confirm"
            >
              Delete Permanently
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* =========================================================================
 * v109.9.106 — Submit-for-Approval dialog. Asks the owner to confirm they
 * sent the settlement e-mail + provide the sender e-mail id so Accounts can
 * cross-reference. On confirm, POSTs /submit-for-approval and the row
 * transitions Downloaded → Submitted with a fresh approval chain.
 * =======================================================================*/
function SubmitForApprovalDialog({ row, onClose, onSubmitted }) {
  const { user } = useAuth();
  const [sent, setSent] = useState(false);
  const [senderEmail, setSenderEmail] = useState(user?.email || "");
  const [busy, setBusy] = useState(false);

  const doSubmit = async () => {
    if (!sent) { toast.error("Please confirm you have sent the e-mail"); return; }
    if (!senderEmail || !senderEmail.includes("@")) { toast.error("Please provide a valid sender e-mail id"); return; }
    setBusy(true);
    try {
      await api.post(`/travel-settlements/${row.id}/submit-for-approval`, {
        email_sent: true,
        sender_email: senderEmail.trim(),
      });
      toast.success("Submitted for approval");
      onSubmitted?.();
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent className="max-w-lg" data-testid="ts-submit-approval-dialog">
        <DialogHeader>
          <DialogTitle className="heading">Submit for Approval</DialogTitle>
          <p className="text-[12px] text-slate-500">
            Settlement Code: <b className="mono text-indigo-700">{row.settlement_code}</b>
          </p>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <label className="flex items-start gap-2 rounded-md border border-slate-200 dark:border-slate-700 p-2.5 cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800/40">
            <input
              type="checkbox"
              checked={sent}
              onChange={(e) => setSent(e.target.checked)}
              className="mt-0.5"
              data-testid="ts-confirm-email-sent"
            />
            <div className="text-[12.5px] text-slate-700 dark:text-slate-200">
              I confirm that I have sent the settlement PDF + all supporting proofs to my
              <b> Reporting Manager</b> (with the Assigned-By user, Master Admins and
              <span className="mono"> accounts@techknowskola.com </span> in CC).
            </div>
          </label>
          <div>
            <Label className="text-[11px] uppercase tracking-wider font-semibold text-slate-600">
              Your e-mail id (the address you sent from) *
            </Label>
            <Input
              type="email"
              value={senderEmail}
              onChange={(e) => setSenderEmail(e.target.value)}
              className="mt-1.5"
              placeholder="you@panchakosh.com"
              data-testid="ts-sender-email"
            />
            <div className="text-[10.5px] text-slate-500 mt-1">
              Accounts uses this to cross-reference the e-mail in their inbox.
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button
            onClick={doSubmit}
            disabled={busy || !sent}
            className="bg-indigo-600 hover:bg-indigo-700 text-white"
            data-testid="ts-submit-approval-confirm"
          >
            <Send size={13} className="mr-1.5" /> {busy ? "Submitting…" : "Submit"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

